import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, TrendingUp, Mountain, ArrowUpRight, Play } from "lucide-react";
import { useMarket } from "../../lib/market";

const CAT_LABEL = {
  trekking: "Trekking", wellness: "Wellness", motorcycle: "Motorcycle",
  flying: "Flying", culture: "Cultural",
};

export default function TourCard({ tour, index = 0 }) {
  const { price } = useMarket();
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.45, delay: (index % 3) * 0.06 }}
    >
      <Link
        to={`/tours/${tour.slug}`}
        data-testid={`tour-card-${tour.slug}`}
        className="group flex flex-col h-full rounded-md overflow-hidden border border-sand-dark bg-white hover:border-terracotta transition-colors"
      >
        <div className="relative h-52 overflow-hidden">
          <img src={tour.hero_image} alt={tour.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/55 to-transparent" />
          <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-white/90 text-ink px-2 py-1 rounded-sm">
            {CAT_LABEL[tour.category] || tour.category}
          </span>
          <div className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-sm flex items-center justify-center">
            <ArrowUpRight className="w-4 h-4 text-ink group-hover:text-terracotta transition-colors" />
          </div>
          {tour.video_review_count > 0 && (
            <span role="link" tabIndex={0} data-testid={`tour-card-video-badge-${tour.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/tours/${tour.slug}#video-reviews`); }}
              className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-terracotta text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm shadow hover:bg-terracotta-hover transition-colors">
              <Play className="w-3 h-3" fill="currentColor" /> Video reviews · {tour.video_review_count}
            </span>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-display font-bold text-lg tracking-tight leading-snug">{tour.name}</h3>
          <p className="text-sm text-ink-muted mt-0.5">{tour.subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-auto pt-4 font-mono text-[11px] text-ink-secondary">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-moss" />{tour.days > 1 ? `${tour.days}d` : "1 day"}</span>
            <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-moss" />{tour.difficulty}</span>
            {tour.altitude_max > 0 && <span className="flex items-center gap-1"><Mountain className="w-3.5 h-3.5 text-moss" />{tour.altitude_max}m</span>}
          </div>
          <div className="mt-3 pt-3 border-t border-sand-dark flex items-baseline justify-between" data-testid={`tour-price-${tour.slug}`}>
            {tour.price_from ? (
              <span className="text-sm text-ink-secondary">From <span className="font-display font-extrabold text-base text-terracotta">{price(tour.price_from)}</span> <span className="text-xs text-ink-muted">pp</span></span>
            ) : (
              <span className="text-sm text-ink-muted">Price on request</span>
            )}
            <span className="font-mono text-[10px] uppercase tracking-widest text-moss">View →</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
